<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="visible"
    modal
    :header="$t('pdfTools.saveAs.title')"
    class="dialog-save-as"
    @update:visible="$emit('close')"
    @show="onDialogShow"
  >
    <div class="save-as-form">
      <div class="form-field">
        <label for="filename">{{ $t("pdfTools.saveAs.filenameLabel") }}</label>
        <PrimeInputText id="filename" v-model="filename" />
      </div>

      <div class="form-field">
        <label for="location">{{ $t("pdfTools.saveAs.folderLabel") }}</label>
        <div class="input-group">
          <PrimeInputText id="location" readonly :value="selectedFolderName" />
          <PrimeButton
            :icon="
              isExplorerVisible ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            "
            :label="$t('pdfTools.saveAs.browse')"
            @click="isExplorerVisible = !isExplorerVisible"
          />
        </div>
      </div>

      <div v-if="isExplorerVisible" class="explorer-container">
        <TreeExplorer
          :current-node="initialNode"
          show-user-home
          :show-files="false"
          @selectNode="onNodeSelect"
        />
      </div>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('pdfTools.common.cancel')"
        icon="ri-close-line"
        text
        @click="$emit('close')"
      />
      <PrimeButton
        :label="$t('pdfTools.common.save')"
        icon="ri-check-line"
        @click="save"
        :disabled="!filename || !selectedFolderId"
      />
    </template>
  </PrimeDialog>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import PrimeDialog from "primevue/dialog";
import PrimeInputText from "primevue/inputtext";
import PrimeButton from "primevue/button";
import TreeExplorer from "@/components/TreeExplorer.vue";
import { useI18n } from "vue-i18n";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

const { t } = useI18n();

const props = defineProps({
  visible: Boolean,
  defaultFilename: {
    type: String,
    default: null,
  },
  parentFolderNode: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close", "save"]);

const filename = ref("");
const initialNode = ref(null);
const isExplorerVisible = ref(false);
const selectedFolderId = ref(null);
const selectedFolderName = ref("");

const onDialogShow = () => {
  filename.value =
    props.defaultFilename || t("pdfTools.saveAs.defaultFilename");
  initialNode.value = props.parentFolderNode;
  selectedFolderId.value = props.parentFolderNode?.id;
  selectedFolderName.value = props.parentFolderNode?.name;
  isExplorerVisible.value = false;
};

const onNodeSelect = async (nodeData) => {
  if (!nodeData || !nodeData.isFolder) {
    return;
  }
  if (nodeData.name === "documentLibrary") {
    selectedFolderId.value = nodeData.id;
    if (nodeData.parentId) {
      try {
        const parentNode = await alfrescoNodeService.getNode(nodeData.parentId);
        selectedFolderName.value =
          parentNode.properties["cm:title"] || parentNode.name;
      } catch (e) {
        console.error("Could not fetch parent node name", e);
        selectedFolderName.value = nodeData.name;
      }
    } else {
      selectedFolderName.value = nodeData.name;
    }
  } else {
    selectedFolderId.value = nodeData.id;
    selectedFolderName.value = nodeData.name;
  }
  isExplorerVisible.value = false;
};

const save = () => {
  if (selectedFolderId.value) {
    emit("save", {
      filename: filename.value,
      folderId: selectedFolderId.value,
    });
  }
};
</script>

<!-- Non-scoped: Dialog teleported to body -->
<style>
.dialog-save-as {
  width: 35rem;
}
</style>

<style scoped>
.save-as-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-field label {
  font-weight: 500;
  font-size: 0.875rem;
}

.input-group {
  display: flex;
}

.input-group :deep(.p-inputtext) {
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.input-group :deep(.p-button) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.explorer-container {
  margin-top: 0.75rem;
  border: 1px solid var(--p-surface-border);
  padding: 0.5rem;
  overflow-y: auto;
  max-height: 20rem;
  border-radius: 0.375rem;
}
</style>
